import { useEffect, useMemo, useState } from 'react';
import { useCore } from '$api-clients/hooks';
import {
  buildLeaseSearchResult,
  buildListingsSearchResult,
  buildOffersSearchResult,
} from './helpers';
import type { SearchResult } from './types';

export function useSearchInteractions() {
  const [searchValue, setSearchValue] = useState('');
  const [listings, setListings] = useState<SearchResult[]>([]);
  const [offers, setOffers] = useState<SearchResult[]>([]);
  const [leases, setLeases] = useState<SearchResult[]>([]);

  const queryParams = useMemo(() => {
    if (!searchValue) return undefined;

    return {
      offset: 0,
      limit: 3,
      search: searchValue,
      facets: 'isArchived:false',
      includeContacts: true,
    };
  }, [searchValue]);

  const { data: leasesData, isLoading: loadingLeases } = useCore(
    '/interactions',
    queryParams
      ? {
          params: {
            query: { ...queryParams, type: 'lease' },
          },
        }
      : null,
  );

  const { data: listingsData, isLoading: loadingListings } = useCore(
    '/listings',
    queryParams
      ? {
          params: {
            query: queryParams,
          },
        }
      : null,
  );

  const { data: offersData, isLoading: loadingOffers } = useCore(
    '/offers',
    queryParams
      ? {
          params: {
            query: queryParams,
          },
        }
      : null,
  );

  useEffect(() => {
    if (offersData) {
      setOffers(buildOffersSearchResult(offersData.results));
    }
  }, [offersData]);

  useEffect(() => {
    if (listingsData) {
      setListings(buildListingsSearchResult(listingsData.results));
    }
  }, [listingsData]);

  useEffect(() => {
    if (leasesData) {
      setLeases(buildLeaseSearchResult(leasesData.results));
    }
  }, [leasesData]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);

    if (value === '') {
      setListings([]);
      setOffers([]);
      setLeases([]);
    }
  };

  const results = useMemo(() => {
    return [...listings, ...offers, ...leases];
  }, [listings, offers, leases]);

  const totals = useMemo(() => {
    if (!searchValue) return { listings: 0, offers: 0, leases: 0 };

    return {
      listings: listingsData?.count ?? 0,
      offers: offersData?.count ?? 0,
      leases: leasesData?.count ?? 0,
    };
  }, [listingsData, offersData, leasesData, searchValue]);

  return {
    setSearchValue: handleSearchChange,
    loading: loadingLeases || loadingListings || loadingOffers,
    searchValue,
    results,
    totals,
  };
}
